import { useEffect, useRef, useState } from "react";
import type { BreakpointState } from "../../hooks/useBreakpoint";
import { ContainerBreakpointContext } from "../../hooks/useContainerBreakpoint";

/**
 * ContainerQueryProvider — measures its own width via ResizeObserver and
 * provides breakpoint state to all descendant blocks via context.
 *
 * Wrap block previews (or board surfaces) in this provider so blocks that
 * use `useContainerBreakpoint` respond to their actual render width, not
 * the browser viewport.
 *
 * Renders a transparent div wrapper — apply className to control layout.
 */
export function ContainerQueryProvider({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<BreakpointState>({
    current: "base",
    isSm: false,
    isMd: false,
    isLg: false,
    isXl: false,
    is2Xl: false,
    isMobile: true,
    isDesktop: false,
  });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      const width = el.offsetWidth;
      const isSm = width >= 640;
      const isMd = width >= 768;
      const isLg = width >= 1024;
      const isXl = width >= 1280;
      const is2Xl = width >= 1536;

      let current: BreakpointState["current"] = "base";
      if (is2Xl) current = "2xl";
      else if (isXl) current = "xl";
      else if (isLg) current = "lg";
      else if (isMd) current = "md";
      else if (isSm) current = "sm";

      setState({ current, isSm, isMd, isLg, isXl, is2Xl, isMobile: !isMd, isDesktop: isMd });
    };

    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <ContainerBreakpointContext.Provider value={state}>
      <div ref={ref} className={className} style={{ width: "100%" }}>
        {children}
      </div>
    </ContainerBreakpointContext.Provider>
  );
}
